import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DeleteProductWarningComponent } from 'components/admin/product/delete-product-warning.component';
import { ListProductsComponent } from 'components/admin/product/list-products.component';
import { SelectVariantOptionsComponent } from 'components/admin/product/select-variant-options.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { TagsComponent } from 'components/input/tags/tags.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { ProductTagConstants } from 'constants/ProductTagConstants';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductTagModel } from 'models/generated/Entities.GormCrm.ProductTag';
import { ProductVariantModel } from 'models/generated/Entities.GormCrm.ProductVariant';
import { VariantListModel } from 'models/generated/Entities.GormCrm.VariantList';
import { VariantListOptionModel } from 'models/generated/Entities.GormCrm.VariantListOption';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NumberOptions, NumberService } from 'services/number.service';
import { ProductService } from 'services/product.service';

@Component({
	selector: 'edit-product',
	templateUrl: './edit-product.component.html',
	providers: [FormValidationService],
	imports: [Layout, TagsComponent, CustomInputComponent, CustomSelectComponent, InputStateDirective, FormsModule, ValidationDirective, SelectVariantOptionsComponent, TranslatePipe]
})
@ComponentUrl('edit-product')
@CredentialRequired(CredentialType.ProductsAccessFull)
export class EditProductComponent {

	@ViewChild('newProductVariantOptions') newProductVariantOptions?: SelectVariantOptionsComponent;
	@ViewChild('variantContainer', { read: ElementRef }) variantContainer?: ElementRef<HTMLElement>;

	@UrlParam
	set productId(id: string) {
		if (id) {
			this.loadingProduct = true;
			this.product = undefined;
			this.productService.getProduct(id).then(product => {
				this.loadProductModel(product);
				this.filterVariantListsByUse();
				this.filterActiveProductVariants();
			});
		}
		this.navigation.openParentView(this, 'editproduct', ListProductsComponent).then(view => (view.selectedProductId = id));
	}
	get productId() {
		return this.product?.id ?? '';
	}

	get newProduct() {
		return this.product && !this.product.id;
	}

	get productName(): string | undefined {
		return this.product?.name;
	}

	loadingData = true;
	loadingProduct = false;
	inputTypeEnum = InputType;
	variantLists?: VariantListModel[];
	variantListsInUseBySelectedProduct: VariantListModel[] = [];
	activeVariants?: ProductVariantModel[];	
	visibleVariants: ProductVariantModel[] = [];
	product?= new ProductModel();	
	productTags: ProductTagModel[];
	otherProducts: SelectListItemModel[] = [];
	accountingPlanSetups: SelectListItemModel[] = [];
	constructor(private readonly navigation: NavigationService,
		public readonly productService: ProductService,
		private readonly accountingService: AccountingService,
		private readonly numberService: NumberService,
		private readonly formValidation: FormValidationService) {

		this.productTags = ProductPlanTagConstants.values.filter(tag => tag != ProductPlanTagConstants.prefixForProductTags)
			.map(tag => ({ tag: `${ProductPlanTagConstants.prefixForProductTags}${tag}` } as ProductTagModel))
			.concat(ProductTagConstants.values.map(tag => ({ tag: tag } as ProductTagModel)));

		Promise.all([
			this.productService.getAllProducts().then(products => {
				this.otherProducts = products.map(p => new SelectListItemModel(p.id!, p.name));
			}),
			this.accountingService.getAccountingPlanSetups(true).then(setups => {
				this.accountingPlanSetups = setups.sort((a, b) => { return a.name.localeCompare(b.name); }).map(s => new SelectListItemModel(s.id!, s.name));
			}),
			this.productService.getAllVariantLists().then(variantLists => {
				this.variantLists = variantLists;
				this.filterVariantListsByUse();
			})

		]).then(() => this.loadingData = false);
	}

	filterVariantListsByUse() {
		if (!this.product || !this.variantLists) return;
		this.variantListsInUseBySelectedProduct = [];
		this.variantLists.forEach(variantList => {
			if (this.product!.productVariants.some(pv => pv.productVariantsOptions.some(pvo => pvo.variantOption!.variantListId === variantList.id))) {
				this.variantListsInUseBySelectedProduct.push(variantList);
			}
		});
	};

	loadProductModel(productModel: ProductModel) {
		this.product = productModel;
		this.loadingProduct = false;
		this.navigation.refreshUrl();
	}

	deleteProduct = () => {
		if (!this.product) return;
		return this.navigation.openPopup(DeleteProductWarningComponent).then(d => {
			d.productId = this.product?.id;
			d.productName = this.product?.name;
		});
	};

	saveProduct = () => {
		if (!this.product) return;

		this.product.include(p => p.productTags);
		this.product.include(p => p.productVariants);

		this.product.productVariants.forEach(variant => {
			variant.include(pv => pv.productVariantsOptions);
			variant.productVariantsOptions.forEach(option => option.include(pvo => pvo.variantOption));
		});

		let productId = this.product.id as string;
		return this.productService.updateProduct(this.product).then(() => {
			this.productId = productId;
		});
	};

	createProduct = () => {
		if (!this.product) return;

		this.generateVariants(this.newProductVariantOptions);

		this.product.include(p => p.productTags);
		this.product.include(p => p.productVariants);

		this.product.productVariants.forEach(variant => {
			variant.include(pv => pv.productVariantsOptions);
			variant.productVariantsOptions.forEach(option => option.include(pvo => pvo.variantOption));
		});

		return this.productService.createProduct(this.product).then(product => {
			this.productId = product.id!;
		});
	};

	generateVariants(variantOptionSelector?: SelectVariantOptionsComponent) {
		if (!this.variantLists || !this.product) return;

		const checkedVariantOptions: VariantListOptionModel[][] = [];

		this.variantLists.forEach((variantList: VariantListModel) => {
			const options: VariantListOptionModel[] = [];

			variantList.variantListOptions.forEach(option => {
				if (!variantOptionSelector?.isSelected(option)) return;
				option.include(o => o.variantList);
				options.push(option);
			});

			if (options.length !== 0) checkedVariantOptions.push(options);
		});

		const newVariants = this.productService.generateVariantCombinations(checkedVariantOptions);

		// If any combination of variant list options exist on the product already, reuse their IDs and price
		for (const newVariant of newVariants) {
			const existingVariant = this.product.productVariants.find(pv =>
				!pv.deleted &&
				pv.productVariantsOptions.length === newVariant.productVariantsOptions.length &&
				pv.productVariantsOptions.every(pvo => newVariant.productVariantsOptions.some(nv => nv.variantOption?.id === pvo.variantOption?.id)));

			if (existingVariant) {
				newVariant.id = existingVariant.id;
				newVariant.price = existingVariant.price;
			}
		}

		this.product.productVariants = newVariants;
		this.filterActiveProductVariants();
	}

	private filterActiveProductVariants(){
		this.activeVariants = this.product?.productVariants
			.filter(pv => pv.deleted == null && !pv.productVariantsOptions.find(op => op.variantOption?.deleted)) ?? [];

		this.productService.sortVariants(this.activeVariants, this.variantListsInUseBySelectedProduct);
		this.visibleVariants = this.activeVariants.slice(0, 50);
	}

	public scrolledVariants() {
		if (!this.variantContainer || !this.activeVariants || this.activeVariants.length == this.visibleVariants.length) return;

		const containerElement = this.variantContainer.nativeElement;
		const scrollBottom = containerElement.scrollTop + containerElement.offsetHeight;
		if (scrollBottom < containerElement.scrollHeight - 100) return;

		this.visibleVariants.push(...this.activeVariants.slice(this.visibleVariants.length, this.visibleVariants.length + 50));
	}

	openVariantOptionSelection() {
		this.navigation.openPopup(SelectVariantOptionsComponent).then(view => {
			if (!this.product) return;
			view.showSaveButton = true;
			view.variantLists = [...this.variantListsInUseBySelectedProduct];
			view.selectedVariantOptions = this.product.productVariants
				.filter(pv => pv.deleted == null)
				.flatMap(pv => pv.productVariantsOptions.map(pvo => pvo.variantOption!.id!));

			view.selectionConfirmed.subscribe(() => {
				// Update variant list on product model, mark form as dirty and close the popup
				this.generateVariants(view);
				this.formValidation.fieldStateChanged(this, true, false);
				this.navigation.closeView(view);
			});
		});
	}

	numberOptions = new NumberOptions(false);
	getCurrency(variant: ProductVariantModel) {
		if (variant.displayPrice) return variant.displayPrice;
		return variant.displayPrice = this.numberService.formatNumber(variant.price, 2, this.numberOptions);
	}
	setCurrency(variant: ProductVariantModel, value: string) {
		variant.displayPrice = value;
		variant.price = this.numberService.getNumber(value, this.numberOptions);
	}

	markAsPristine() { }
}
